/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState } from "react";
import {
  CalendarIcon,
  NeedHelp,
  PlusIcon,
  SelectArrow,
  TickIcon,
} from "../../atoms/CustomIcons";
import DashboardTemplate from "../../templates/Dashboard";
import Header from "../../atoms/Header";
import { Button, DatePicker, DatePickerProps, Drawer, Select } from "antd";
import dayjs from "dayjs";
import { DUMMY_DATA } from "./dummyData";
import KanbanBoard from "./Component/KanbanBoard";
import EmptyCard from "../../atoms/EmptyCard";
import TimeWrapper from "./Component/TimeWrapper";
import RoutesMap from "./Component/RoutesMap";
import ScheduleWrapper from "./Component/ScheduleWrapper";
import DateNavigatorLeft from "../../atoms/CustomIcons/DateNavigatorLeft";
import DateNavigatorRight from "../../atoms/CustomIcons/DateNavigatorRight";
import LocationIcon from "../../atoms/CustomIcons/LocationIcon";
import ScheduleIcon from "../../atoms/CustomIcons/ScheduleIcon";
import WeatherWrapper from "./Component/WeatherWrapper";

interface Visit {
  id: string;
  title: string;
  status: string;
  startTime: string;
  endTime: string;
  address: string;
  contract?: string;
}

interface Crew {
  id: string;
  name: string;
  crew_leader: string;
  hours: string;
  visit?: Visit[] | undefined; // Visit is optional as it can be dragged out
}

interface Manager {
  id: string;
  name: string;
  crew: Crew[];
}

interface ServiceLine {
  id: string;
  name: string;
  managers: Manager[];
}

export default function Scheduling() {
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const [loading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs().toDate());
  const [serviceLineData, setServiceLineData] = useState<{
    dropdown: { label: string; value: string }[];
    selectedServiceLine: ServiceLine[];
    selectedDropdownValue: string | null;
  }>({
    dropdown: [],
    selectedServiceLine: [],
    selectedDropdownValue: null,
  });
  const [managerData, setManagerData] = useState<{
    dropdown: { label: string; value: string }[];
    selectedManager: Manager[];
    selectedDropdownValue: string | null;
  }>({
    dropdown: [],
    selectedManager: [],
    selectedDropdownValue: null,
  });
  const [crewData, setCrewData] = useState<Crew[]>();
  const [columnData, setColumnData] = useState<any[]>();
  const [showRoutes, setShowRoutes] = useState(false);
  const [showScheduled, setShowScheduled] = useState(false);
  const [scheduleId, setScheduleId] = useState<string | null>(null);
  const [scheduleData, setScheduleData] = useState<any[]>([]);

  console.log("Company ID:", serviceLineData, scheduleId);

  useEffect(() => {
    const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
    const filteredItem = DUMMY_DATA?.find(
      (item: any) => item.date === formattedDate
    );
    const filteredData = filteredItem?.serviceLines;

    setServiceLineData({
      selectedServiceLine: filteredData || [],
      dropdown:
        filteredData?.map((item: any) => ({
          label: item.name,
          value: item.id,
        })) || [],
      selectedDropdownValue:
        serviceLineData.selectedDropdownValue !== null
          ? serviceLineData.selectedDropdownValue
          : filteredData?.[0]?.id || null,
    });
  }, [selectedDate]);

  useEffect(() => {
    if (DUMMY_DATA && serviceLineData.selectedDropdownValue) {
      const transformedCrewData: any[] = [];

      DUMMY_DATA.forEach((daySchedule) => {
        const date = daySchedule.date; // Get the date for the current day's schedule
        daySchedule.serviceLines.forEach((serviceLine, serviceIndex) => {
          if (serviceLine.id === serviceLineData.selectedDropdownValue) {
            serviceLine.managers.forEach((manager) => {
              manager.crew.forEach((crew, crewIndex) => {
                // Find if this crew already exists in our transformed data
                const existingCrew: any = transformedCrewData.find(
                  (c) => c.id === crew.id
                );

                // Map visits to ensure their 'start' and 'end' times include the correct date
                const visitsForThisDate = crew.visit.map((visit, i) => {
                  // Parse the time part from the existing visit.start/end
                  const visitStartTime = dayjs(visit.startTime, "h:mm A");
                  const visitEndTime = dayjs(visit.endTime, "h:mm A");

                  // Set the hour, minute, second on the specific date
                  const startDateTime = dayjs(date)
                    .set("hour", visitStartTime.hour())
                    .set("minute", visitStartTime.minute())
                    .set("second", visitStartTime.second())
                    .format();

                  const endDateTime = dayjs(date)
                    .set("hour", visitEndTime.hour())
                    .set("minute", visitEndTime.minute())
                    .set("second", visitEndTime.second())
                    .format();

                  return {
                    ...visit,
                    id:
                      crypto.randomUUID() ??
                      `visit-${serviceIndex + 1}-${crewIndex + 1}-${i + 1}`, // Ensure unique ID
                    start: startDateTime,
                    end: endDateTime,
                    // Add other properties as needed for FullCalendarEvent and extendedProps
                    editable: true,
                    extendedProps: {
                      type: "visit",
                      location: visit.address,
                      backgroundColor: "#E8F5E9",
                      borderColor: "#4CAF50",
                      textColor: "#1B5E20",
                    },
                  };
                });

                // Add dummy crew members if the crew is new
                const crewMembers = existingCrew
                  ? existingCrew.crewMembers
                  : [
                      {
                        id: "member-001",
                        name: "Jake Ryan",
                        image: "/images/dummy/crew1.png",
                        role: "5678, Crew leader",
                        punchedIn: "6:38 AM",
                        crewLeader: true,
                        crewLeaderId: "crew-a-001",
                      },
                      {
                        id: "member-002",
                        name: "Carlos Eduardo",
                        image: "/images/dummy/crew2.png",
                        latePunchedIn: "7:59 AM",
                        role: "5679",
                        crewLeaderId: "crew-a-002",
                      },
                      {
                        id: "member-003",
                        name: "Tyler James",
                        image: "/images/dummy/crew3.png",
                        punchedIn: "7:25 AM",
                        role: "1265",
                        crewLeaderId: "crew-a-003",
                      },
                      {
                        id: "member-004",
                        name: "Mateo Luis",
                        image: "/images/dummy/crew4.png",
                        role: "2904",
                        crewLeaderId: "crew-a-004",
                      },
                    ];

                if (existingCrew) {
                  // If the crew exists, append the visits for the current date
                  existingCrew.visit.push(...visitsForThisDate);
                } else {
                  // If the crew does not exist, create a new entry
                  transformedCrewData.push({
                    id: crew.id,
                    name: crew.name,
                    crew_leader: crew.crew_leader,
                    hours: crew.hours,
                    crewMembers: crewMembers, // Add dummy crew members
                    visit: visitsForThisDate,
                  });
                }
              });
            });
          }
        });
      });
      setScheduleData(transformedCrewData);
    }
  }, [setScheduleData, serviceLineData.selectedDropdownValue]);

  useEffect(() => {
    if (serviceLineData.selectedServiceLine.length > 0) {
      const selectedServiceLine = serviceLineData.selectedServiceLine.find(
        (item: any) => item.id === serviceLineData.selectedDropdownValue
      );

      const mappedManagers =
        selectedServiceLine?.managers?.map((manager: any) => ({
          label: manager.name,
          value: manager.id,
        })) || [];

      setManagerData({
        dropdown: mappedManagers,
        selectedManager: selectedServiceLine?.managers || [],
        selectedDropdownValue: mappedManagers?.[0]?.value || null,
      });
    } else {
      setManagerData({
        dropdown: [],
        selectedManager: [],
        selectedDropdownValue: null,
      });
    }
  }, [serviceLineData]);

  useEffect(() => {
    if (managerData.selectedManager.length > 0) {
      const selectedManager = managerData.selectedManager.find(
        (item: any) => item.id === managerData.selectedDropdownValue
      );
      setCrewData(selectedManager?.crew || []);
    } else {
      setCrewData([]);
    }
  }, [managerData.selectedManager, managerData.selectedDropdownValue]);

  const onDateChange: DatePickerProps["onChange"] = (date) => {
    if (date) {
      setSelectedDate(date.toDate());
    }
  };

  const serviceLineChangeHandler = (value: string) => {
    setServiceLineData((prev) => ({
      ...prev,
      selectedDropdownValue: value,
    }));
  };

  const managerChangeHandler = (value: string) => {
    setManagerData((prev) => ({
      ...prev,
      selectedDropdownValue: value,
    }));
  };

  // console.log("serviceLineData:", serviceLineData);
  // console.log("managerData:", managerData);
  // console.log("crewData:", crewData);
  // console.log("columnData", columnData);

  const onClose = () => {
    setShowScheduled(false);
  };

  console.log("columnData", columnData);
  console.log("scheduleData", scheduleData);
  console.log("DUMMY_DATA", DUMMY_DATA);
  return (
    <DashboardTemplate loading={loading}>
      <Header
        title="Schedule"
        description=""
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />

      <div className="scheduleWrapper">
        <div className="scheduleHeader">
          <div className="leftHeader">
            <DatePicker
              onChange={onDateChange}
              value={dayjs(selectedDate)}
              format={"ddd, MMM DD"}
              allowClear={false}
              suffixIcon={<CalendarIcon />}
            />
            <div className="dateWrapper">
              <Button
                icon={<DateNavigatorLeft />}
                type="text"
                className="only_icon previous_btn"
                onClick={() =>
                  setSelectedDate(
                    dayjs(selectedDate).subtract(1, "day").toDate()
                  )
                }
              />
              <div className="datePickerWrapper">
                <span className="day">
                  {dayjs(selectedDate).format("ddd")},
                </span>
                <span className="date">
                  {dayjs(selectedDate).format("MMM")}
                </span>
                <span className="date">{dayjs(selectedDate).format("DD")}</span>
              </div>
              <Button
                icon={<DateNavigatorRight />}
                type="text"
                className="only_icon next_btn"
                onClick={() =>
                  setSelectedDate(dayjs(selectedDate).add(1, "day").toDate())
                }
              />
            </div>
            <TimeWrapper />
            <WeatherWrapper />
            <Select
              style={{ width: "190px" }}
              suffixIcon={<SelectArrow />}
              menuItemSelectedIcon={<TickIcon style={{ color: "#00A07D" }} />}
              showSearch
              allowClear
              onChange={serviceLineChangeHandler}
              value={serviceLineData.selectedDropdownValue}
              placeholder="All service lines"
              options={serviceLineData.dropdown}
            />
            <Select
              style={{ width: "150px" }}
              suffixIcon={<SelectArrow />}
              menuItemSelectedIcon={<TickIcon style={{ color: "#00A07D" }} />}
              showSearch
              allowClear
              onChange={managerChangeHandler}
              value={managerData.selectedDropdownValue}
              placeholder="Managers"
              options={managerData.dropdown}
            />

            <div className="filterDownload">
              <Button
                onClick={() => setShowRoutes(!showRoutes)}
                icon={<LocationIcon />}
                style={{ height: "36px", width: "36px", borderRadius: "10px" }}
              />
            </div>
          </div>

          <div className="rightHeader">
            <Button htmlType="button" className="outlined-btn" size="large">
              <PlusIcon /> Task
            </Button>
            <Button
              type="primary"
              onClick={() => (window.location.href = `/scheduler/${companyId}`)}
            >
              <ScheduleIcon /> Schedule work
            </Button>
          </div>
        </div>

        <div className="kanbanWrapper">
          {crewData && crewData.length > 0 ? (
            <KanbanBoard
              crewData={crewData}
              setColumnData={setColumnData}
              setScheduleId={setScheduleId}
              setShowScheduled={setShowScheduled}
              selectedDate={selectedDate}
            />
          ) : (
            <div className="empty_wrap">
              <EmptyCard
                imageHeight={220}
                title="No crews available"
                description="You currently have no crews assigned to work with."
                image="/images/empty-icons/empty-schedule.svg"
                h2Style={{
                  fontSize: "18px",
                  color: "#474C4D",
                  lineHeight: "26px",
                }}
                pStyle={{ color: "#474C4D" }}
              />
            </div>
          )}
          {showRoutes && (
            <div className="routesBlackWrapper">
              <div className="routesWrapper">
                <RoutesMap
                  routeData={columnData}
                  setShowRoutes={setShowRoutes}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Drawer
        placement="bottom"
        width="100%"
        onClose={onClose}
        open={showScheduled}
        className="drawer-bottom"
        destroyOnHidden={true}
        maskClosable={false}
        mask={false}
        closable={false}
      >
        <ScheduleWrapper
          crewData={scheduleData}
          onClose={() => setShowScheduled(false)}
        />
      </Drawer>
    </DashboardTemplate>
  );
}
